import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft,
  Download,
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  Check,
  X as XIcon,
  FilePlus,
  FileText,
  ChevronDown,
  ChevronUp,
  Mail,
  Printer,
} from "lucide-react";
import { CURRENCIES } from "@/utils/currencies";
import { validateDueDate } from "@/utils/validation";
import {
  formatServiceEntityLabel,
} from "@/utils/invoiceServiceDescription";
import { toLocalDateInputValue } from "@/utils/date";

export default function ViewInvoice() {
  const { id } = useParams();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [settingsInvoiceNotes, setSettingsInvoiceNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [canEdit, setCanEdit] = useState(false);
  const [canApprove, setCanApprove] = useState(false);

  const [formData, setFormData] = useState({
    currency: "OMR",
    vat_percentage: 5.0,
    discount: 0.0,
    due_date: "",
    notes: "",
  });
  const [pricingView, setPricingView] = useState({ mode: "sell_only", show_markup: false });
  const [approvalFlags, setApprovalFlags] = useState({
    manager_approved: false,
    accountant_approved: false,
  });
  const PRICING_VIEW_LABELS = { sell_only: "Sell price only", cost_and_sell: "Cost + sell", margin: "Margin / markup %", hide_prices: "Hide prices (quotation)" };

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [vendorNameById, setVendorNameById] = useState({});

  const [services, setServices] = useState([]);
  const [expandedServiceRows, setExpandedServiceRows] = useState(new Set());
  const [showServiceCost, setShowServiceCost] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [invoiceTemplate, setInvoiceTemplate] = useState({
    primary_color: "#0891b2",
    table_header_bg: "#e5e7eb",
    table_header_text: "#0891b2",
    show_logo: true,
    footer_text: "",
  });
  const [refundAllocations, setRefundAllocations] = useState([]);

  useEffect(() => {
    // Finance and Admin can edit invoices
    setCanEdit(user?.role === "finance" || user?.role === "admin");
    // Finance, Management and Admin can approve/reject pending invoices
    setCanApprove(
      user?.role === "finance" ||
        user?.role === "management" ||
        user?.role === "admin",
    );
  }, [user]);

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [invoiceData, settings, tmpl] = await Promise.all([
        api.invoices.getById(id),
        api.settings.getCompany().catch(() => ({ currency: "OMR", vat_rate: 5 })),
        api.settings.getInvoiceTemplate().catch(() => ({})),
      ]);
      const allocationRows = await api.payments
        .getRefundAllocations({ invoice_id: id })
        .catch(() => []);
      setInvoice(invoiceData);
      setRefundAllocations(Array.isArray(allocationRows) ? allocationRows : []);
      setInvoiceTemplate((prev) => ({
        ...prev,
        ...(tmpl && typeof tmpl === "object" ? tmpl : {}),
      }));
      let bd = settings?.bank_details;
      if (typeof bd === "string") {
        try {
          bd = bd ? JSON.parse(bd) : null;
        } catch (_) {
          bd = null;
        }
      }
      const hasAnyBankDetail = bd && typeof bd === "object" && Object.values(bd).some((v) => v != null && String(v).trim() !== "");
      setBankDetails(hasAnyBankDetail ? bd : null);
      setSettingsInvoiceNotes((settings?.invoice_notes || "").trim());
      const defaultCurrency = settings?.currency || "OMR";
      const defaultVat = settings?.vat_rate != null ? Number(settings.vat_rate) : 5.0;
      setFormData({
        currency: invoiceData.currency || defaultCurrency,
        vat_percentage: invoiceData.vat_percentage != null ? parseFloat(invoiceData.vat_percentage) : defaultVat,
        discount: Number(invoiceData.discount) || 0,
        due_date: invoiceData.due_date || "",
        notes: invoiceData.notes || "",
      });
      setServices(
        (invoiceData.services || [{ name: "", amount: 0 }]).map((s) => ({
          ...s,
          name: s.name,
          description: s.description,
          notes: s.notes,
          amount: s.amount,
          cost: s.cost,
          vendor_id: s.vendor_id,
          vendor_name: s.vendor_name,
          service_type: s.service_type,
          extra_fields: s.extra_fields,
          vendors: s.vendors,
          passenger_assignments: s.passenger_assignments,
          service_entities: s.service_entities,
        })),
      );
      setExpandedServiceRows(new Set());
      const pv = invoiceData.pricing_view
        ? (typeof invoiceData.pricing_view === "string" ? JSON.parse(invoiceData.pricing_view) : invoiceData.pricing_view)
        : { mode: "sell_only", show_markup: false };
      setPricingView(pv);
      setApprovalFlags({
        manager_approved: Boolean(invoiceData.manager_approved),
        accountant_approved: Boolean(invoiceData.accountant_approved),
      });

      // Fetch customer details
      if (invoiceData.customer_id) {
        try {
          const customerData = await api.customers.getById(
            invoiceData.customer_id
          );
          setCustomer(customerData);
        } catch (err) {
          console.error("Failed to fetch customer:", err);
        }
      }
    } catch (error) {
      console.error("Failed to fetch invoice:", error);
      setError(error.message || "Failed to load invoice");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const needsVendorLookup = services.some((service) => {
      if (!Array.isArray(service.vendors) || service.vendors.length === 0) return false;
      return service.vendors.some(
        (vendor) =>
          vendor?.vendor_id &&
          !String(vendor?.vendor_name || vendor?.name || "").trim(),
      );
    });

    if (!needsVendorLookup) {
      setVendorNameById({});
      return undefined;
    }

    const fetchVendors = async () => {
      try {
        const rows = await api.vendors.getAll();
        if (cancelled) return;
        const lookup = {};
        (Array.isArray(rows) ? rows : []).forEach((vendor) => {
          const id = vendor?.id;
          const name =
            vendor?.name ||
            vendor?.company_name ||
            vendor?.vendor_name ||
            null;
          if (id && name) lookup[id] = String(name);
        });
        setVendorNameById(lookup);
      } catch (_) {
        if (!cancelled) setVendorNameById({});
      }
    };

    fetchVendors();
    return () => {
      cancelled = true;
    };
  }, [services]);

  const getDisplayVendorName = useCallback(
    (vendor) => {
      const direct = vendor?.vendor_name || vendor?.name;
      if (direct && String(direct).trim()) return String(direct).trim();
      const id = vendor?.vendor_id;
      if (id && vendorNameById[id]) return vendorNameById[id];
      return "";
    },
    [vendorNameById],
  );

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "vat_percentage" || name === "discount"
          ? parseFloat(value) || 0
          : value,
    }));
  };

  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...services];
    updatedServices[index] = {
      ...updatedServices[index],
      [field]: field === "amount" ? parseFloat(value) || 0 : value,
    };
    setServices(updatedServices);
  };

  const addService = () => {
    setServices([
      ...services,
      { name: "", amount: 0, quantity: 1, vendor_id: undefined, vendor_name: undefined },
    ]);
  };

  const removeService = (index) => {
    if (services.length > 1) {
      setServices(services.filter((_, i) => i !== index));
    }
  };

  const calculateSubtotal = () => {
    return services.reduce((sum, service) => sum + (service.amount || 0), 0);
  };

  const handleSave = async () => {
    setError("");
    setSubmitting(true);

    // Validation
    if (services.some((s) => !s.name || !s.amount)) {
      setError("All services must have a name and amount");
      setSubmitting(false);
      return;
    }
    const dueErr = validateDueDate(formData.due_date);
    if (dueErr) {
      setError(dueErr);
      setSubmitting(false);
      return;
    }

    try {
      const updatedInvoice = {
        services: services.map((s) => ({
          name: s.name,
          amount: parseFloat(s.amount) || 0,
          cost: s.cost != null ? parseFloat(s.cost) : null,
          vendor_id: s.vendor_id,
          vendor_name: s.vendor_name,
        })),
        currency: formData.currency,
        vat_percentage: Number(formData.vat_percentage) ?? 0,
        discount: Number(formData.discount) || 0,
        due_date: formData.due_date || null,
        notes: formData.notes || null,
        pricing_view: pricingView,
        manager_approved: approvalFlags.manager_approved,
        accountant_approved: approvalFlags.accountant_approved,
      };

      await api.invoices.update(id, updatedInvoice);
      await api.invoices.updateApprovalFlags(id, {
        manager_approved: approvalFlags.manager_approved,
        accountant_approved: approvalFlags.accountant_approved,
      });
      setEditing(false);
      fetchInvoice(); // Refresh invoice data
      setSuccessMessage("Invoice updated successfully!");
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Failed to update invoice:", error);
      setError(error.message || "Failed to update invoice. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = () => {
    setConfirmMessage(
      `Are you sure you want to approve invoice ${invoice.invoice_number}?`
    );
    setConfirmAction(() => async () => {
      try {
        await api.invoices.approve(id);
        setShowConfirmModal(false);
        setSuccessMessage("Invoice approved successfully!");
        setShowSuccessModal(true);
        fetchInvoice();
      } catch (error) {
        console.error("Failed to approve invoice:", error);
        setShowConfirmModal(false);
        setError(
          error.message || "Failed to approve invoice. Please try again."
        );
      }
    });
    setShowConfirmModal(true);
  };

  const handleReject = () => {
    setConfirmMessage(
      `Are you sure you want to reject invoice ${invoice.invoice_number}?`
    );
    setConfirmAction(() => async () => {
      try {
        await api.invoices.reject(id);
        setShowConfirmModal(false);
        setSuccessMessage("Invoice rejected.");
        setShowSuccessModal(true);
        fetchInvoice();
      } catch (error) {
        console.error("Failed to reject invoice:", error);
        setShowConfirmModal(false);
        setError(
          error.message || "Failed to reject invoice. Please try again."
        );
      }
    });
    setShowConfirmModal(true);
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.invoices.exportPDF(id);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download PDF:", error);
      setError("Failed to download invoice PDF. Please try again.");
    }
  };

  const handlePrintInvoice = async () => {
    setError("");
    try {
      const response = await api.invoices.exportPDF(id);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (w) {
        w.addEventListener("load", () => {
          try {
            w.focus();
            w.print();
          } catch (_) {
            /* ignore */
          }
        });
      }
    } catch (error) {
      console.error("Failed to open PDF for print:", error);
      setError("Failed to open PDF for print. Please try again.");
    }
  };

  const openMailtoFallback = () => {
    const email = customer?.email;
    if (!email) return;
    const subj = encodeURIComponent(`Invoice ${invoice?.invoice_number || ""}`);
    const body = encodeURIComponent(
      `Dear ${customer?.name || "Customer"},\n\n` +
        `Please refer to invoice ${invoice?.invoice_number || ""}.\n\n` +
        `We can provide a PDF copy on request.\n\nThank you.`,
    );
    window.location.href = `mailto:${encodeURIComponent(
      email,
    )}?subject=${subj}&body=${body}`;
  };

  const handleEmailCustomer = async () => {
    setError("");
    if (!customer?.email?.trim()) {
      setError(
        "This customer has no email on file. Add an email in Customers, then try again.",
      );
      return;
    }
    setSendingEmail(true);
    try {
      const result = await api.invoices.sendEmail(id);
      const extra =
        result?.to && typeof result.to === "string" ? ` (${result.to})` : "";
      setSuccessMessage(
        (result?.message || "Email sent with PDF attached.") + extra,
      );
      setShowSuccessModal(true);
    } catch (err) {
      setError(
        err?.message ||
          "Could not send the invoice by email. Check SMTP in Settings, then try again.",
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleServiceExpand = (index) => {
    setExpandedServiceRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-emerald-100 text-emerald-700",
      pending_approval: "bg-amber-100 text-amber-700",
      rejected: "bg-red-100 text-red-700",
      draft: "bg-stone-100 text-stone-700",
    };
    return (
      <span
        className={`rounded-full px-3 py-1 text-sm font-medium uppercase tracking-wide ${
          styles[status] || styles.draft
        }`}
      >
        {status.replace("_", " ")}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const styles = {
      paid: "bg-emerald-100 text-emerald-700",
      partial: "bg-amber-100 text-amber-700",
      partially_refunded: "bg-amber-100 text-amber-700",
      refunded: "bg-purple-100 text-purple-700",
      unpaid: "bg-red-100 text-red-700",
      pending: "bg-red-100 text-red-700",
    };
    return (
      <span
        className={`rounded-full px-3 py-1 text-sm font-medium uppercase tracking-wide ${
          styles[status] || styles.pending
        }`}
      >
        {String(status || "pending").replace(/_/g, " ")}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/invoices"
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-stone-600" />
          </Link>
          <h1 className="text-2xl font-heading font-semibold text-secondary">
            Invoice Details
          </h1>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  const subtotal = calculateSubtotal();
  const vatAmount =
    (subtotal * (parseFloat(formData.vat_percentage) || 0)) / 100;
  const discount = Number(formData.discount) || 0;
  const total = subtotal + vatAmount - discount;
  const amountPaid = Math.max(0, parseFloat(invoice.amount_paid || 0));
  const refundedAmount = Math.max(0, parseFloat(invoice.refunded_total || 0));
  const invoiceTypeLabel =
    invoice.invoice_type === "tax" || invoice.status === "approved"
      ? "Tax Invoice"
      : "Proforma Invoice";
  const displayPaymentStatus =
    refundedAmount > 0
      ? amountPaid <= 0
        ? "refunded"
        : amountPaid < total
          ? "partially_refunded"
          : "paid"
      : invoice.payment_status;

  return (
    <div className="space-y-6">
      {/* Parent / Add-on notice (GAP 5) */}
      {invoice.parent_invoice_id && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-lg px-4 py-3 flex items-center gap-2">
          <FileText size={20} className="text-cyan-600" />
          <span className="text-cyan-800">
            This is an add-on invoice.{" "}
            <Link to={`/invoices/${invoice.parent_invoice_id}`} className="font-medium underline hover:no-underline">
              View parent invoice
            </Link>
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/invoices"
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-stone-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-heading font-semibold text-secondary">
              {invoiceTypeLabel} {invoice.invoice_number}
              {invoice.parent_invoice_id && (
                <span className="ml-2 text-sm font-normal text-stone-500">(Add-on)</span>
              )}
            </h1>
            <p className="text-sm text-stone-600 mt-1">
              Created on{" "}
              {new Date(invoice.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/workflow-tickets?source_type=invoice&source_id=${encodeURIComponent(id)}&title=${encodeURIComponent(`Invoice ${invoice.invoice_number} follow-up`)}&description=${encodeURIComponent(`Created from invoice ${invoice.invoice_number} for customer ${invoice.customer_name || ""}`)}&to_role=finance`}
            className="px-3 py-2 border border-cyan-300 text-cyan-700 rounded-lg hover:bg-cyan-50 transition-colors text-sm font-medium"
            title="Create workflow ticket from this invoice"
          >
            Create Ticket
          </Link>
          {canApprove && !editing && invoice.status === "pending_approval" && (
            <>
              <button
                onClick={handleApprove}
                className="p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                title="Approve Invoice"
              >
                <Check size={20} />
              </button>
              <button
                onClick={handleReject}
                className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                title="Reject Invoice"
              >
                <XIcon size={20} />
              </button>
            </>
          )}
          {canEdit && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="p-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
              title="Edit Invoice"
            >
              <Edit size={20} />
            </button>
          )}
          {editing && (
            <>
              <button
                onClick={() => {
                  setEditing(false);
                  fetchInvoice(); // Reset form
                }}
                className="p-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                title="Cancel Editing"
              >
                <X size={20} />
              </button>
              <button
                onClick={handleSave}
                disabled={submitting}
                className="p-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-50"
                title="Save Changes"
              >
                <Save size={20} />
              </button>
            </>
          )}
          {invoice.status === "approved" && (user?.role === "sales" || user?.role === "admin") && (
            <Link
              to={`/invoices/new?parent_invoice_id=${id}&customer_id=${invoice.customer_id || ""}`}
              className="p-2 border border-cyan-300 text-cyan-700 rounded-lg hover:bg-cyan-50 transition-colors flex items-center gap-1.5"
              title="Create add-on invoice"
            >
              <FilePlus size={20} />
              <span className="text-sm font-medium">Add-on</span>
            </Link>
          )}
          <button
            type="button"
            onClick={handleEmailCustomer}
            disabled={sendingEmail}
            className="p-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors disabled:opacity-50"
            title="Email invoice PDF to the customer (uses SMTP in Settings)"
          >
            <Mail size={20} className={sendingEmail ? "opacity-50" : ""} />
          </button>
          <button
            type="button"
            onClick={handlePrintInvoice}
            className="p-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
            title="Print invoice (PDF)"
          >
            <Printer size={20} />
          </button>
          <button
            onClick={handleDownloadPDF}
            className="p-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
            title="Download PDF"
          >
            <Download size={20} />
          </button>
        </div>
      </div>

      {invoice.child_invoice_ids && invoice.child_invoice_ids.length > 0 && (
        <div className="bg-stone-50 rounded-lg border border-stone-200 p-4">
          <h3 className="text-sm font-semibold text-stone-700 mb-2">Add-on invoices</h3>
          <ul className="flex flex-wrap gap-2">
            {invoice.child_invoice_ids.map((childId) => (
              <li key={childId}>
                <Link
                  to={`/invoices/${childId}`}
                  className="text-primary font-medium hover:underline"
                >
                  View invoice {childId.slice(0, 8)}…
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg space-y-2">
          <p>{error}</p>
          {/SMTP|Outgoing email|not configured/i.test(error) && customer?.email && (
            <button
              type="button"
              onClick={openMailtoFallback}
              className="text-sm font-medium text-red-900 underline hover:no-underline"
            >
              Compose in your email app instead (no PDF attachment)
            </button>
          )}
        </div>
      )}

      {/* Status Badges */}
      <div className="flex items-center gap-4">
        <div>
          <span className="text-sm text-stone-600 mr-2">Status:</span>
          {getStatusBadge(invoice.status)}
        </div>
        <div>
          <span className="text-sm text-stone-600 mr-2">Payment:</span>
          {getPaymentStatusBadge(displayPaymentStatus)}
        </div>
      </div>

      {/* Invoice Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company & Customer Info */}
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-secondary mb-4">
              Bill To
            </h2>
            {customer ? (
              <div className="space-y-2 text-stone-700">
                <p className="font-semibold text-lg">{customer.name}</p>
                {customer.company && <p>{customer.company}</p>}
                {customer.email && <p>📧 {customer.email}</p>}
                {customer.phone && <p>📞 {customer.phone}</p>}
                {customer.address && <p>📍 {customer.address}</p>}
              </div>
            ) : (
              <p className="text-stone-600">{invoice.customer_name}</p>
            )}
          </div>
        </div>

        {/* Invoice Settings */}
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-secondary mb-4">
            Invoice Details
          </h2>
          {editing ? (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Approvals
                </label>
                <div className="space-y-2 rounded-lg border border-stone-200 px-3 py-2">
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <input
                      type="checkbox"
                      checked={approvalFlags.manager_approved}
                      onChange={(e) =>
                        setApprovalFlags((prev) => ({
                          ...prev,
                          manager_approved: e.target.checked,
                        }))
                      }
                    />
                    Manager approved
                  </label>
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <input
                      type="checkbox"
                      checked={approvalFlags.accountant_approved}
                      onChange={(e) =>
                        setApprovalFlags((prev) => ({
                          ...prev,
                          accountant_approved: e.target.checked,
                        }))
                      }
                    />
                    Accountant approved
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Currency
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                >
                  {CURRENCIES.map((curr) => (
                    <option key={curr.code} value={curr.code}>
                      {curr.code} - {curr.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    VAT (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    name="vat_percentage"
                    value={formData.vat_percentage}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Discount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    name="discount"
                    value={formData.discount === undefined || formData.discount === null ? "" : formData.discount}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Pricing view (PDF / display)
                </label>
                <select
                  value={pricingView.mode || "sell_only"}
                  onChange={(e) => setPricingView((p) => ({ ...p, mode: e.target.value }))}
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                >
                  <option value="sell_only">Sell price only</option>
                  <option value="cost_and_sell">Cost + sell price</option>
                  <option value="margin">Margin / markup %</option>
                  <option value="hide_prices">Hide prices (quotation)</option>
                </select>
                {pricingView.mode === "margin" && (
                  <label className="flex items-center gap-2 mt-2 text-sm text-stone-600">
                    <input
                      type="checkbox"
                      checked={pricingView.show_markup === true}
                      onChange={(e) => setPricingView((p) => ({ ...p, show_markup: e.target.checked }))}
                      className="rounded"
                    />
                    Show as markup %
                  </label>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Payment Due Date
                </label>
                <input
                  type="date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleInputChange}
                  min={toLocalDateInputValue()}
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none resize-none"
                />
              </div>
            </>
          ) : (
            <div className="space-y-2 text-stone-700">
              <p>
                <span className="font-medium">Manager approval:</span>{" "}
                {approvalFlags.manager_approved ? "Yes" : "No"}
              </p>
              <p>
                <span className="font-medium">Accountant approval:</span>{" "}
                {approvalFlags.accountant_approved ? "Yes" : "No"}
              </p>
              <p>
                <span className="font-medium">Pricing view:</span>{" "}
                {PRICING_VIEW_LABELS[pricingView.mode] || "Sell price only"}
              </p>
              <p>
                <span className="font-medium">Currency:</span>{" "}
                {invoice.currency}
              </p>
              <p>
                <span className="font-medium">VAT:</span>{" "}
                {invoice.vat_percentage}%
              </p>
              {Number(invoice.discount) > 0 && (
                <p>
                  <span className="font-medium">Discount:</span>{" "}
                  {invoice.currency} {(Number(invoice.discount) || 0).toFixed(2)}
                </p>
              )}
              {invoice.due_date && (
                <p>
                  <span className="font-medium">Payment Due Date:</span>{" "}
                  {new Date(invoice.due_date).toLocaleDateString()}
                </p>
              )}
              {settingsInvoiceNotes && (
                <div>
                  <span className="font-medium">Notes:</span>
                  <p className="mt-1 text-stone-600 whitespace-pre-wrap">{settingsInvoiceNotes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bank details (only if configured in Settings) */}
      {bankDetails && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary mb-3">Bank details</h2>
          <p className="text-sm text-stone-600 mb-4">
            Kindly transfer the amount mentioned in this invoice to our account as follows:
          </p>
          <div className="text-sm text-stone-700 space-y-1">
            {bankDetails.bankName && <p><span className="font-medium">Bank Name:</span> {bankDetails.bankName}</p>}
            {bankDetails.address && <p><span className="font-medium">Address:</span> {bankDetails.address}</p>}
            {bankDetails.branch && <p><span className="font-medium">Branch:</span> {bankDetails.branch}</p>}
            {bankDetails.beneficiaryName && <p><span className="font-medium">Beneficiary Name:</span> {bankDetails.beneficiaryName}</p>}
            {bankDetails.iban && <p><span className="font-medium">IBAN:</span> {bankDetails.iban}</p>}
            {bankDetails.accountNo && <p><span className="font-medium">Account No:</span> {bankDetails.accountNo}</p>}
            {bankDetails.swiftCode && <p><span className="font-medium">Swift Code:</span> {bankDetails.swiftCode}</p>}
          </div>
        </div>
      )}

      {/* Services */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-secondary mb-4">Services</h2>
        {editing ? (
          <div className="space-y-4">
            {services.map((service, index) => (
              <div
                key={index}
                className="grid grid-cols-12 gap-4 items-center p-4 bg-stone-50 rounded-lg"
              >
                <div className="col-span-8">
                  <input
                    type="text"
                    value={service.name}
                    onChange={(e) =>
                      handleServiceChange(index, "name", e.target.value)
                    }
                    placeholder="Service name"
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div className="col-span-3">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={service.amount}
                    onChange={(e) =>
                      handleServiceChange(index, "amount", e.target.value)
                    }
                    placeholder="Amount"
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div className="col-span-1">
                  {services.length > 1 && (
                    <button
                      onClick={() => removeService(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button
              onClick={addService}
              className="flex items-center gap-2 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
            >
              <Plus size={18} />
              Add Service
            </button>
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <p className="form-helper max-w-2xl">
                Use the arrow to expand a line and see all entered details (tickets, PNR, dates, multi-vendor lines). Turn on
                &quot;Show cost&quot; to see buy / cost when recorded.
              </p>
              <label className="inline-flex items-center gap-2 text-sm text-stone-700 cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  className="rounded border-stone-300"
                  checked={showServiceCost}
                  onChange={(e) => setShowServiceCost(e.target.checked)}
                />
                Show cost (buy) prices
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead
                  className="border-b border-stone-200"
                  style={{
                    backgroundColor: invoiceTemplate.table_header_bg || "#e5e7eb",
                    color:
                      invoiceTemplate.table_header_text ||
                      invoiceTemplate.primary_color ||
                      "#0891b2",
                  }}
                >
                  <tr>
                    <th className="w-10 px-2 py-3" aria-label="Expand" />
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase">Vendor</th>
                    {(showServiceCost ||
                      pricingView.mode === "cost_and_sell" ||
                      pricingView.mode === "margin") && (
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase">
                        <span className="block">Cost</span>
                        <span className="block text-[10px] font-normal opacity-80 normal-case">
                          ({invoice.currency || "OMR"})
                        </span>
                      </th>
                    )}
                    {pricingView.mode !== "hide_prices" && (
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase">
                        <span className="block">Amount</span>
                        <span className="block text-[10px] font-normal opacity-80 normal-case">
                          ({invoice.currency || "OMR"})
                        </span>
                      </th>
                    )}
                    {pricingView.mode === "margin" && (
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase">
                        {pricingView.show_markup ? "Markup %" : "Margin %"}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {services.map((service, index) => {
                    const showCostCol =
                      showServiceCost ||
                      pricingView.mode === "cost_and_sell" ||
                      pricingView.mode === "margin";
                    const cost = service.cost != null ? parseFloat(service.cost) : null;
                    const amount = parseFloat(service.amount || 0);
                    const marginPct =
                      pricingView.mode === "margin" && cost != null && (pricingView.show_markup ? cost > 0 : amount > 0)
                        ? pricingView.show_markup
                          ? ((amount - cost) / cost) * 100
                          : ((amount - cost) / amount) * 100
                        : null;
                    const isOpen = expandedServiceRows.has(index);
                    const colCount =
                      1 +
                      1 +
                      1 +
                      (showCostCol ? 1 : 0) +
                      (pricingView.mode !== "hide_prices" ? 1 : 0) +
                      (pricingView.mode === "margin" ? 1 : 0);
                    const formatPairs = (obj) => {
                      if (!obj || typeof obj !== "object") return [];
                      return Object.entries(obj).filter(
                        ([, v]) => v != null && String(v).trim() !== "",
                      );
                    };
                    const vList = Array.isArray(service.vendors) ? service.vendors : [];
                    const vendorSummary =
                      vList.length === 0
                        ? service.vendor_name || "—"
                        : (() => {
                            const names = vList
                              .map((v) => getDisplayVendorName(v))
                              .filter(Boolean);
                            if (names.length) return names.join(" · ");
                            return `${vList.length} ${vList.length === 1 ? "vendor" : "vendors"}`;
                          })();
                    return (
                      <>
                        <tr key={`svc-${index}`} className="hover:bg-stone-50/80">
                          <td className="px-2 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => toggleServiceExpand(index)}
                              className="p-1.5 text-stone-600 hover:bg-stone-100 rounded-lg"
                              title={isOpen ? "Collapse details" : "Expand details"}
                            >
                              {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                            </button>
                          </td>
                          <td className="px-4 py-4 text-stone-800 text-sm break-words max-w-md">
                            <span className="font-medium text-secondary">
                              {service.name || service.description || "Service"}
                            </span>
                            {service.service_type && (
                              <span className="ml-2 text-xs text-stone-500 capitalize">
                                ({String(service.service_type).replace(/_/g, " ")})
                              </span>
                            )}
                            {service.notes && (
                              <p className="mt-1 text-xs text-stone-500 line-clamp-2">
                                {String(service.notes)}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-4 text-stone-600 text-sm max-w-[200px] break-words">
                            {vendorSummary}
                          </td>
                          {showCostCol && (
                            <td className="px-4 py-4 text-right font-mono text-stone-600 text-sm">
                              {cost != null ? cost.toFixed(2) : "—"}
                            </td>
                          )}
                          {pricingView.mode !== "hide_prices" && (
                            <td className="px-4 py-4 text-right font-mono font-semibold text-secondary">
                              {amount.toFixed(2)}
                            </td>
                          )}
                          {pricingView.mode === "margin" && (
                            <td className="px-4 py-4 text-right font-mono text-stone-600 text-sm">
                              {marginPct != null ? `${marginPct.toFixed(1)}%` : "—"}
                            </td>
                          )}
                        </tr>
                        {isOpen && (
                          <tr key={`svc-${index}-detail`} className="bg-stone-50/80">
                            <td colSpan={colCount} className="px-4 py-4 text-sm text-stone-700 border-t border-stone-100">
                              {service.service_type && (
                                <p className="mb-2">
                                  <span className="font-medium text-stone-800">Service type: </span>
                                  {String(service.service_type).replace(/_/g, " ")}
                                </p>
                              )}
                              {formatPairs(service.extra_fields).length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                                    Line details (shared)
                                  </p>
                                  <ul className="space-y-1 list-disc pl-5">
                                    {formatPairs(service.extra_fields).map(([k, v]) => (
                                      <li key={k}>
                                        <span className="text-stone-500">{k.replace(/_/g, " ")}: </span>
                                        {String(v)}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {Array.isArray(service.passenger_assignments) &&
                                service.passenger_assignments.length > 0 && (
                                  <div className="mb-3">
                                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                                      Passengers
                                    </p>
                                    <ul className="list-disc pl-5 space-y-0.5">
                                      {service.passenger_assignments.map((p, pi) => (
                                        <li key={pi}>
                                          {p.full_name || p.name || p.passenger_id}
                                          {p.role ? ` (${p.role})` : ""}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              {Array.isArray(service.service_entities) &&
                                service.service_entities.length > 0 && (
                                  <div className="mb-3">
                                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                                      Linked services / segments
                                    </p>
                                    <ul className="list-disc pl-5 space-y-0.5">
                                      {service.service_entities.map((ent, ei) => (
                                        <li key={ent.id || ei}>{formatServiceEntityLabel(ent)}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              {vList.length > 0 && (
                                <div className="space-y-3">
                                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                                    Vendors (lines)
                                    <span className="ml-2 font-normal text-stone-400">
                                      (amounts in {invoice.currency || "OMR"})
                                    </span>
                                  </p>
                                  {vList.map((v, vi) => (
                                    <div
                                      key={vi}
                                      className="rounded-lg border border-stone-200 bg-white px-3 py-2"
                                    >
                                      <p className="font-medium text-stone-800">
                                        {getDisplayVendorName(v) || "Vendor"}{" "}
                                        {v.vendor_id && (
                                          <span className="text-xs font-mono text-stone-500">({String(v.vendor_id).slice(0, 8)}…)</span>
                                        )}
                                      </p>
                                      <p className="text-sm text-stone-600 mt-1 font-mono">
                                        Buy:{" "}
                                        {(Math.round((parseFloat(v.buy_price ?? v.cost ?? 0) || 0) * 100) / 100).toFixed(2)} — Sell:{" "}
                                        {(Math.round((parseFloat(v.sell_price ?? v.sale_amount ?? 0) || 0) * 100) / 100).toFixed(2)}
                                      </p>
                                      {v.extra_fields && formatPairs(v.extra_fields).length > 0 && (
                                        <ul className="mt-2 space-y-0.5 text-sm pl-3 list-disc">
                                          {formatPairs(v.extra_fields).map(([k, val]) => (
                                            <li key={k}>
                                              <span className="text-stone-500">{k.replace(/_/g, " ")}: </span>
                                              {String(val)}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {formatPairs(service.extra_fields).length === 0 &&
                                vList.length === 0 &&
                                !service.service_type &&
                                !(Array.isArray(service.passenger_assignments) && service.passenger_assignments.length) &&
                                !(Array.isArray(service.service_entities) && service.service_entities.length) && (
                                  <p className="text-stone-500">No extra fields stored for this line.</p>
                                )}
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
        <div className="flex justify-end">
          <div className="w-full max-w-md space-y-3">
            <div className="flex justify-between text-stone-700">
              <span>Subtotal:</span>
              <span className="font-mono">
                {formData.currency} {subtotal.toFixed(2)}
              </span>
            </div>
            {(parseFloat(formData.vat_percentage) || 0) > 0 && (
              <div className="flex justify-between text-stone-700">
                <span>VAT ({parseFloat(formData.vat_percentage) || 0}%):</span>
                <span className="font-mono">
                  {formData.currency} {vatAmount.toFixed(2)}
                </span>
              </div>
            )}
            {discount > 0 && (
              <div className="flex justify-between text-stone-700">
                <span>Discount:</span>
                <span className="font-mono text-red-600">
                  -{formData.currency} {discount.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between pt-3 border-t-2 border-stone-200">
              <span className="text-lg font-semibold text-secondary">
                Total:
              </span>
              <span className="text-lg font-semibold font-mono text-primary">
                {formData.currency} {total.toFixed(2)}
              </span>
            </div>
            {refundedAmount > 0 && (
              <div className="flex justify-between text-stone-700 pt-2 border-t border-stone-200">
                <span>Refunded Amount:</span>
                <span className="font-mono font-semibold text-red-600">
                  -{formData.currency} {refundedAmount.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-stone-700">
              <span>Paid Amount:</span>
              <span className="font-mono font-semibold text-emerald-700">
                {formData.currency} {amountPaid.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {refundAllocations.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary mb-3">
            Refund Allocation Details
          </h2>
          <p className="text-sm text-stone-600 mb-4">
            Line-level reversal details recorded for this invoice refund activity.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-3 py-2 text-left">Refund Date</th>
                  <th className="px-3 py-2 text-left">Service</th>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-right">Sell Reversed</th>
                  <th className="px-3 py-2 text-right">Cost Reversed</th>
                  <th className="px-3 py-2 text-right">Vendor Cash Received</th>
                </tr>
              </thead>
              <tbody>
                {refundAllocations.map((row) => (
                  <tr key={row.id} className="border-t border-stone-100">
                    <td className="px-3 py-2">{String(row.refund_date || "").slice(0, 10) || "-"}</td>
                    <td className="px-3 py-2">{row.service_name || "-"}</td>
                    <td className="px-3 py-2">{row.vendor_name || "-"}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {Number(row.refund_sell_amount || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {Number(row.refund_cost_amount || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {Number(row.vendor_cash_received_amount || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-secondary mb-4">
                Confirm Action
              </h3>
              <p className="text-stone-700 mb-6">{confirmMessage}</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setConfirmAction(null);
                    setConfirmMessage("");
                  }}
                  className="px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (confirmAction) {
                      await confirmAction();
                    }
                  }}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg transition-colors"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-emerald-100 rounded-full">
                  <Check className="text-emerald-600" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-secondary">
                  Success
                </h3>
              </div>
              <p className="text-stone-700 mb-6">{successMessage}</p>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSuccessMessage("");
                  }}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
